import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { FaSyncAlt } from 'react-icons/fa';
import { FaFileExcel } from 'react-icons/fa6';
import { MdSchedule } from 'react-icons/md';
import AdvanceFilterDrawer from 'components/common/AdvanceFilterDrawer';
import { PermissionContext } from 'context/Context';
import { exportProjectScheduleOrderEntry } from 'redux/crm/report/reportSlice';
import { getActiveSalesUnits } from 'redux/crm/salesUnitSlice';
import { getBuildingStatuses } from 'redux/crm/settingSlice';
import useScheduleFilters, {
  FILTER_FIELDS,
  DIVIDER_AFTER,
} from 'components/app/crm/reports/project_schedule_order_entry/useScheduleFilters';
import ScheduleFilterBar from 'components/app/crm/reports/project_schedule_order_entry/ScheduleFilterBar';
import DetailTab from 'components/app/crm/reports/project_schedule_order_entry/DetailTab';

// The objective KPIs (Schedule / Order Entry / JAF vs EST) used to be tabs of
// this report; they now live on the Change Order Dashboard.
const CO_DASHBOARD_PERMISSION = 'v507';
const CO_DASHBOARD_PATH = '/dashboard/crm/change-order';

const ProjectScheduleOEPage = () => {
  const dispatch = useDispatch();
  const { projectScheduleData: { exporting = false } } = useSelector((s) => s.crm_report);
  const { permissions = {} } = useContext(PermissionContext);

  const filters = useScheduleFilters();
  const [reloadToken, setReloadToken] = useState(0);
  const [countLabel, setCountLabel] = useState('');

  useEffect(() => { dispatch(getActiveSalesUnits()); dispatch(getBuildingStatuses()); }, [dispatch]);

  const { filterParams, filterKey } = filters;

  const handleExport = useCallback(() => {
    dispatch(exportProjectScheduleOrderEntry(filterParams));
  }, [dispatch, filterParams]);

  return (
    <div className="tw-flex tw-flex-col tw-gap-3 tw-p-4">

      {/* Header */}
      <div className="tw-mb-1">
        <div className="tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-3">
          <div className="tw-flex tw-items-center tw-gap-3">
            <div className="tw-w-1.5 tw-h-10 tw-rounded-sm tw-flex-shrink-0" style={{ background: 'linear-gradient(to bottom, #0ea5e9, #6366f1)' }} />
            <div className="tw-flex tw-items-center tw-gap-2">
              <MdSchedule className="tw-text-blue-600" size={22} />
              <div>
                <h1 className="tw-mb-0 tw-font-bold tw-text-2xl tw-text-gray-900">Project Schedule (Order Entry)</h1>
                <p className="tw-text-gray-500 tw-text-sm tw-mb-0">
                  Project schedule tracking for Order Entry — follow change orders per building phase.
                  {permissions[CO_DASHBOARD_PERMISSION] === true && (
                    <>
                      {' '}Objective KPIs are on the{' '}
                      <Link to={CO_DASHBOARD_PATH} className="tw-text-primary-600 hover:tw-underline">Change Order Dashboard</Link>.
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>

          <div className="tw-flex tw-items-center tw-gap-2">
            <button
              type="button"
              onClick={() => setReloadToken((token) => token + 1)}
              className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-sm tw-border tw-border-slate-300 tw-bg-white tw-text-slate-600 hover:tw-bg-slate-50 tw-text-sm tw-cursor-pointer disabled:tw-opacity-50"
            >
              <FaSyncAlt size={12} />
              Reload
            </button>
            <button
              type="button"
              onClick={handleExport}
              disabled={exporting}
              className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-sm tw-border tw-border-green-300 tw-bg-green-50 tw-text-green-700 hover:tw-bg-green-100 tw-text-sm tw-font-medium tw-cursor-pointer disabled:tw-opacity-50"
            >
              <FaFileExcel size={13} />
              {exporting ? 'Exporting...' : 'Export'}
            </button>
          </div>
        </div>
      </div>

      <ScheduleFilterBar
        filters={filters}
        countLabel={countLabel}
        onOpenDrawer={() => filters.setShowFilter(true)}
      />

      <DetailTab
        active
        filterParams={filterParams}
        filterKey={filterKey}
        reloadToken={reloadToken}
        onTotalsChange={setCountLabel}
      />

      <AdvanceFilterDrawer
        show={filters.showFilter}
        onClose={() => filters.setShowFilter(false)}
        onApply={filters.applyFilter}
        onReset={filters.resetFilter}
        fields={FILTER_FIELDS}
        values={filters.filterValues}
        onChange={filters.setFilterValue}
        title="Apply filters to advances"
        description="Establish the contract's filtering criteria"
        dividerAfter={DIVIDER_AFTER}
      />
    </div>
  );
};

export default ProjectScheduleOEPage;
